import { useCallback, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import type { IVendasCreditosResponse } from "@/hooks/vendas-creditos/listar-total-creditos-por-cliente";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { EXTRATO_CREDITO_QUERY_STALE_TIME } from "@/common/const/query-cache";
import {
  handleExtratoMovimentacoesCreditoCliente,
  handleVendasTotalCreditosPorCliente,
} from "@/domain/use-cases/vendas-creditos";
import {
  IFiltrosExtratoCreditoCliente,
  IParametrosConsultaExtratoMovimentacoesCreditoCliente,
  IRespostaExtratoMovimentacoesCreditoCliente,
} from "@/interface/extrato-credito-cliente";

const ITENS_POR_PAGINA_PADRAO_EXTRATO = 10;

type UseExtratoMovimentacoesCreditoClienteArgs = {
  clienteId: number | undefined;
};

export type IOpcaoProdutoFiltroExtrato = {
  produtoId: number;
  nome: string;
};

export function useExtratoMovimentacoesCreditoCliente({
  clienteId,
}: UseExtratoMovimentacoesCreditoClienteArgs) {
  const { token: accessToken } = useStoreZustandUserAuth();

  const [paginaAtualExtrato, setPaginaAtualExtrato] = useState(1);
  const [itensPorPaginaExtrato, setItensPorPaginaExtrato] = useState(
    ITENS_POR_PAGINA_PADRAO_EXTRATO
  );
  const [filtrosExtrato, setFiltrosExtrato] = useState<IFiltrosExtratoCreditoCliente>({});

  const parametrosConsultaExtrato: IParametrosConsultaExtratoMovimentacoesCreditoCliente = useMemo(
    () => ({
      paginaAtual: paginaAtualExtrato,
      itensPorPagina: itensPorPaginaExtrato,
      ordenarPor: "created_at",
      ordemClassificacao: "desc",
      produtoIdFiltro: filtrosExtrato.produtoId,
      tipoOperacaoFiltro: filtrosExtrato.tipoOperacao,
      direcaoMovimentoFiltro: filtrosExtrato.direcaoMovimento,
      dataInicioFiltro: filtrosExtrato.dataInicio,
      dataFimFiltro: filtrosExtrato.dataFim,
    }),
    [paginaAtualExtrato, itensPorPaginaExtrato, filtrosExtrato]
  );

  const chaveCacheExtratoCliente = useMemo(
    () => ["extrato-movimentacoes-credito-cliente", clienteId, parametrosConsultaExtrato] as const,
    [clienteId, parametrosConsultaExtrato]
  );

  const {
    data: respostaExtratoBruta,
    isLoading: carregandoExtrato,
    isFetching: buscandoExtrato,
    isError: erroExtrato,
    refetch: recarregarExtrato,
  } = useQuery<IRespostaExtratoMovimentacoesCreditoCliente | null>({
    queryKey: chaveCacheExtratoCliente,
    enabled: Boolean(clienteId && accessToken),
    queryFn: async () => {
      if (!accessToken || !clienteId) {
        return null;
      }

      const resultado = await handleExtratoMovimentacoesCreditoCliente(
        accessToken,
        clienteId,
        parametrosConsultaExtrato
      );

      if (resultado && typeof resultado === "object" && "data" in resultado) {
        return resultado as IRespostaExtratoMovimentacoesCreditoCliente;
      }

      return null;
    },
    staleTime: EXTRATO_CREDITO_QUERY_STALE_TIME,
  });

  const chaveOpcoesProdutoExtrato = useMemo(
    () => ["extrato-opcoes-produto-cliente", clienteId] as const,
    [clienteId]
  );

  const { data: respostaCreditosCliente, isLoading: carregandoOpcoesProdutoExtrato } =
    useQuery<IVendasCreditosResponse | null>({
      queryKey: chaveOpcoesProdutoExtrato,
      enabled: Boolean(clienteId && accessToken),
      queryFn: async () => {
        if (!accessToken || !clienteId) {
          return null;
        }
        const resultado = await handleVendasTotalCreditosPorCliente(accessToken, clienteId);
        if (resultado && typeof resultado === "object" && "data" in resultado) {
          return resultado as IVendasCreditosResponse;
        }
        return null;
      },
      staleTime: EXTRATO_CREDITO_QUERY_STALE_TIME,
    });

  const opcoesProdutoFiltroExtrato: IOpcaoProdutoFiltroExtrato[] = useMemo(() => {
    const linhas = respostaCreditosCliente?.data?.creditos ?? [];
    return linhas
      .map((linha) => {
        const id = linha.produto_id;
        if (id == null || Number.isNaN(Number(id))) {
          return null;
        }
        return { produtoId: Number(id), nome: linha.tipo };
      })
      .filter((item): item is IOpcaoProdutoFiltroExtrato => item !== null);
  }, [respostaCreditosCliente]);

  const linhasExtratoMovimentacoes = respostaExtratoBruta?.data ?? [];
  const paginacaoExtratoCreditoCliente = respostaExtratoBruta?.pagination;

  const definirPaginaAtualExtrato = useCallback((novaPagina: number) => {
    setPaginaAtualExtrato(Math.max(1, novaPagina));
  }, []);

  const definirFiltrosExtrato = useCallback((patch: Partial<IFiltrosExtratoCreditoCliente>) => {
    setFiltrosExtrato((anterior) => ({ ...anterior, ...patch }));
    setPaginaAtualExtrato(1);
  }, []);

  const limparFiltrosExtrato = useCallback(() => {
    setFiltrosExtrato({});
    setPaginaAtualExtrato(1);
  }, []);

  return {
    linhasExtratoMovimentacoes,
    paginacaoExtratoCreditoCliente,
    carregandoExtrato,
    buscandoExtrato,
    erroExtrato,
    paginaAtualExtrato,
    itensPorPaginaExtrato,
    setItensPorPaginaExtrato,
    definirPaginaAtualExtrato,
    recarregarExtrato,
    filtrosExtrato,
    definirFiltrosExtrato,
    limparFiltrosExtrato,
    opcoesProdutoFiltroExtrato,
    carregandoOpcoesProdutoExtrato,
  };
}
